export default function InheritanceComplexities() {
  const complexities = [
    {
      icon: 'ri-file-list-3-line',
      title: 'Probate Process',
      description: 'Court proceedings can take 6-18 months, delaying your ability to sell and creating ongoing expenses.',
      impact: 'Legal fees, court costs, and administrative delays'
    },
    {
      icon: 'ri-group-line',
      title: 'Multiple Heirs',
      description: 'Coordinating decisions among siblings or family members with different goals and timelines.',
      impact: 'Disagreements on pricing, timing, and distribution'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Carrying Costs',
      description: 'Mortgage payments, property taxes, insurance, and maintenance continue accumulating monthly.',
      impact: '$3,000-$15,000+ per month in ongoing expenses'
    },
    {
      icon: 'ri-tools-line',
      title: 'Deferred Maintenance',
      description: 'Years of postponed repairs and updates that need immediate attention before selling.',
      impact: 'Costly renovations or significantly reduced sale price'
    },
    {
      icon: 'ri-file-damage-line',
      title: 'Title Issues',
      description: 'Unclear ownership, liens, or legal complications that must be resolved before transfer.',
      impact: 'Additional legal costs and transaction delays'
    },
    {
      icon: 'ri-calendar-line',
      title: 'Emotional Burden',
      description: 'Managing a property while grieving, often from a distance, adds stress to an already difficult time.',
      impact: 'Mental exhaustion and family relationship strain'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Understanding the Complexities of Inherited Apartment Buildings
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Inheriting an apartment building brings unique challenges that traditional real estate sales don't address. Here's what you're likely facing.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {complexities.map((item, index) => (
            <div key={index} className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-lg mb-6">
                <i className={`${item.icon} text-3xl`}></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">{item.description}</p>
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm font-semibold text-teal-600">{item.impact}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-10">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-full flex-shrink-0">
              <i className="ri-lightbulb-line text-3xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">The Reality of Inherited Properties</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Most inherited apartment buildings come with a combination of these challenges. The property may have been owned for decades, with outdated systems, deferred maintenance, and below-market rents. Meanwhile, you're dealing with probate court, coordinating with other heirs, and watching carrying costs accumulate.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong className="text-gray-900">Traditional real estate agents often avoid these properties</strong> because they require too much work, take too long to sell, and involve complicated family dynamics. That's where we're different—we specialize in exactly these situations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
